import { ENDPOINTS } from "config/endpoints";
import { useCallback } from "react";
import { useMutation, useQueryClient } from "react-query";
import Error from "types/error";
import client from "utils/clients/client";
import { Request } from "types/request";
import { Response } from "types/response";
import { SnitcherSettings } from "types/snitcherSettings";

export interface PatchSnitcherSettingsInputs {
  subscriptionId: number;
  /**
   * Omitted leaves the stored key untouched, an empty string clears it so the
   * subscription falls back on Crono's internal Snitcher key.
   */
  apiKey?: string;
  /**
   * Omitted leaves the stored value untouched. Mandatory when the subscription
   * has no Snitcher settings yet.
   */
  workspaceUuid?: string;
  active?: boolean;
}

const MUTATION_KEY = "patchSnitcherSettings";

export default function usePatchSnitcherSettings() {
  const queryClient = useQueryClient();

  const patchSnitcherSettings = (inputs: PatchSnitcherSettingsInputs) => {
    const request: Request = {
      url: `${ENDPOINTS.subscription.snitcherSettings}`,
      config: {
        method: "patch",
        data: { data: inputs },
      },
    };
    return client(request);
  };

  const { data, mutate, reset, ...rest } = useMutation<
    Response<SnitcherSettings>,
    Error,
    PatchSnitcherSettingsInputs
  >(patchSnitcherSettings, {
    mutationKey: MUTATION_KEY,
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.subscription.main),
      });
    },
  });

  /**
   * The API key travels as mutation variables, which react-query keeps both on
   * the observer and in the mutation cache for the lifetime of the client —
   * long after the modal that collected it is gone. Callers invoke this once
   * they are done with the result so the key stops sitting in memory.
   */
  const clearApiKeyFromCache = useCallback(() => {
    reset();
    const mutationCache = queryClient.getMutationCache();
    mutationCache
      .getAll()
      .filter((mutation) => mutation.options.mutationKey === MUTATION_KEY)
      .forEach((mutation) => mutationCache.remove(mutation));
  }, [queryClient, reset]);

  return { data, mutate, reset, clearApiKeyFromCache, ...rest };
}
